import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import PasswordStrengthMeter from '../components/common/PasswordStrengthMeter';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const token           = searchParams.get('token');

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    if (!token) { toast.error('Invalid reset link'); navigate('/login'); }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 12) return toast.error('Password must be at least 12 characters');
    if (password !== confirm) return toast.error('Passwords do not match');

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success('Password updated successfully');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err?.message || 'Reset failed — the link may have expired');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    page:   { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', padding: 24 },
    card:   { background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '36px 32px', width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
    logo:   { textAlign: 'center', marginBottom: 28 },
    title:  { margin: '0 0 6px', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' },
    sub:    { margin: '0 0 28px', fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center' },
    label:  { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' },
    input:  { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none', marginBottom: 16 },
    btn:    { width: '100%', padding: '11px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginTop: 4 },
    success: { textAlign: 'center', padding: '20px 0' },
  };

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.success}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}></div>
            <h2 style={{ ...styles.title, marginBottom: 8 }}>Password Updated</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>Redirecting you to login…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <h2 style={styles.title}>Set New Password</h2>
          <p style={styles.sub}>Enter a new password for your ZeusCMS account.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div>
            <label style={styles.label}>New Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Min. 12 characters"
              autoFocus
            />
            <PasswordStrengthMeter password={password} />
          </div>
          <div>
            <label style={styles.label}>Confirm Password</label>
            <input
              style={styles.input}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat new password"
            />
          </div>
          <button style={styles.btn} type="submit" disabled={submitting}>
            {submitting ? 'Updating…' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
